<template>
	<div class="head-image" @click="showUserInfo($event)"
		:style="{ cursor: (isShowUserInfo || canLongPressAt) ? 'pointer' : null }">
		<img class="avatar-image" v-if="showAvatarImage" :key="resolvedUrl" :src="resolvedUrl"
			:style="avatarImageStyle" loading="lazy" @error="onImageError" @load="onImageLoad" />
		<div class="avatar-text" v-else :style="avatarTextStyle">
			{{ avatarText }}</div>
		<div v-show="online" class="online" title="用户当前在线"></div>
		<slot></slot>
	</div>
</template>

<script>
import { isDisplayableMediaUrl } from '@/utils/mediaUrl';

const LONG_PRESS_MS = 2000;
const MOVE_CANCEL_PX2 = 64;

export default {
	name: "headImage",
	data() {
		return {
			colors: ["#5daa31", "#c7515a", "#e03697", "#85029b",
				"#c9b455", "#326eb6"],
			imageLoadFailed: false,
			lastFailedUrl: '',
			pressTimer: null,
			pressPoint: null,
			atTriggered: false,
		}
	},
	props: {
		id: {
			type: Number
		},
		size: {
			type: Number,
			default: 42
		},
		width: {
			type: Number
		},
		height: {
			type: Number
		},
		radius: {
			type: String,
			default: "50%"
		},
		url: {
			type: String
		},
		name: {
			type: String,
			default: null
		},
		online: {
			type: Boolean,
			default: false
		},
		isShowUserInfo: {
			type: Boolean,
			default: true
		},
		isAddFriend: {
			type: Boolean,
			default: false
		},
		enableGroupAt: {
			default: null
		}
	},
	mounted() {
		this.bindPressEvents();
	},
	beforeDestroy() {
		this.unbindPressEvents();
		this.clearPress();
	},
	methods: {
		onImageError() {
			this.lastFailedUrl = this.resolvedUrl || '';
			this.imageLoadFailed = true;
		},
		onImageLoad() {
			this.imageLoadFailed = false;
			this.lastFailedUrl = '';
		},
		bindPressEvents() {
			const el = this.$el;
			if (!el) {
				return;
			}
			el.addEventListener('mousedown', this.onPressStart);
			el.addEventListener('mouseup', this.onPressEnd);
			el.addEventListener('mouseleave', this.onPressEnd);
			el.addEventListener('mousemove', this.onPressMove);
			el.addEventListener('contextmenu', this.onPressEnd);
			el.addEventListener('dragstart', this.onDragStart);
		},
		unbindPressEvents() {
			const el = this.$el;
			if (!el) {
				return;
			}
			el.removeEventListener('mousedown', this.onPressStart);
			el.removeEventListener('mouseup', this.onPressEnd);
			el.removeEventListener('mouseleave', this.onPressEnd);
			el.removeEventListener('mousemove', this.onPressMove);
			el.removeEventListener('contextmenu', this.onPressEnd);
			el.removeEventListener('dragstart', this.onDragStart);
		},
		onDragStart(e) {
			e.preventDefault();
		},
		clearPress() {
			if (this.pressTimer) {
				clearTimeout(this.pressTimer);
				this.pressTimer = null;
			}
			this.pressPoint = null;
		},
		onPressStart(e) {
			if (e.button !== 0 || !this.canLongPressAt) {
				return;
			}
			this.clearPress();
			this.atTriggered = false;
			this.pressPoint = { x: e.clientX, y: e.clientY };
			this.pressTimer = setTimeout(() => {
				this.pressTimer = null;
				this.atTriggered = true;
				this.$eventBus && this.$eventBus.$emit('chat:at-member', {
					userId: this.id,
					name: this.name
				});
			}, LONG_PRESS_MS);
		},
		onPressMove(e) {
			if (!this.pressTimer || !this.pressPoint) {
				return;
			}
			const dx = e.clientX - this.pressPoint.x;
			const dy = e.clientY - this.pressPoint.y;
			if (dx * dx + dy * dy > MOVE_CANCEL_PX2) {
				this.clearPress();
			}
		},
		onPressEnd() {
			this.clearPress();
		},
		showUserInfo(e) {
			if (this.atTriggered) {
				this.atTriggered = false;
				e.preventDefault();
				e.stopPropagation();
				return;
			}
			if (!this.isShowUserInfo) return;
			// 自己点自己不展示任何内容
			if (this.id == this.$store.state.userStore.userInfo.id) return;
			if (this.id && this.id > 0) {
				this.$http({
					url: `/user/find/${this.id}`,
					method: 'get'
				}).then((user) => {
					let pos = {
						x: e.x + 30,
						y: e.y
					}
					this.$store.commit("setUserInfoBoxPos", pos);
					this.$store.commit("showUserInfoBox", { ...user, isAddFriend: this.isAddFriend });
				})
			}
		}
	},
	watch: {
		resolvedUrl(newVal, oldVal) {
			if (newVal !== oldVal) {
				this.imageLoadFailed = false;
				this.lastFailedUrl = '';
			}
		}
	},
	computed: {
		s3Url() {
			return this.$store.state.configStore.s3Url;
		},
		resolvedUrl() {
			return this.$mediaUrl(this.url);
		},
		canLongPressAt() {
			if (this.enableGroupAt === false) {
				return false;
			}
			if (!this.id || this.id <= 0) {
				return false;
			}
			const mine = this.$store.state.userStore.userInfo;
			if (mine && this.id == mine.id) {
				return false;
			}
			if (this.enableGroupAt === true) {
				return true;
			}
			const chat = this.$store.state.chatStore.activeChat;
			return !!(chat && chat.type === 'GROUP');
		},
		showAvatarImage() {
			if (!this.url || !String(this.url).trim()) {
				return false;
			}
			const src = this.resolvedUrl;
			if (!src || !String(src).trim()) {
				return false;
			}
			if (!isDisplayableMediaUrl(this.url, this.s3Url)) {
				return false;
			}
			if (this.imageLoadFailed && this.lastFailedUrl === src) {
				return false;
			}
			return true;
		},
		avatarText() {
			return (this.name || '').substring(0, 2).toUpperCase();
		},
		avatarImageStyle() {
			let w = this.width ? this.width : this.size;
			let h = this.height ? this.height : this.size;
			return `width:${w}px; height:${h}px;
					border-radius: ${this.radius};`
		},
		avatarTextStyle() {
			let w = this.width ? this.width : this.size;
			let h = this.height ? this.height : this.size;
			return `
				  width: ${w}px;height:${h}px;
					background-color: ${this.name ? this.textColor : '#fff'};
					font-size:${w * 0.35}px;
					border-radius: ${this.radius};
					`
		},
		textColor() {
			let hash = 0;
			for (var i = 0; i < this.name.length; i++) {
				hash += this.name.charCodeAt(i);
			}
			return this.colors[hash % this.colors.length];
		}
	}
}
</script>

<style scoped lang="scss">
.head-image {
	position: relative;
	user-select: none;
	-webkit-user-select: none;

	.avatar-image {
		position: relative;
		overflow: hidden;
		display: block;
		background: #f5f5f5;
		object-fit: cover;
	}

	.avatar-text {
		color: white;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.online {
		position: absolute;
		right: -5px;
		bottom: 0;
		width: 12px;
		height: 12px;
		background: limegreen;
		border-radius: 50%;
		border: 2px solid white;
	}
}
</style>
